// ============================================================================
// CASE GROUP HOOKS — Phase A
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { caseGroupService } from '../../api/services/caseGroupService';
import type { ID } from '../../api/types';
import type {
  CaseGroup,
  CaseGroupCandidate,
  CaseGroupCandidateSearchRequest,
  CaseGroupListItem,
  CaseGroupSearchRequest,
  CaseMembership,
} from '../../api/types/caseGroup.types';

const emptyPagination = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0, hasNextPage: false, hasPreviousPage: false,
};

export const useCaseGroups = (params?: CaseGroupSearchRequest) => {
  const [data, setData] = useState<CaseGroupListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState(emptyPagination);

  const key = JSON.stringify(params);
  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await caseGroupService.getAll(params);
      setData(res.data); setPagination(res.pagination);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, pagination, refetch: fetch };
};

/** Single case-group (header + members). refreshKey forces a refetch after member/header changes. */
export const useCaseGroup = (id: ID | undefined, refreshKey?: number) => {
  const [data, setData] = useState<CaseGroup | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!id) { setData(null); return; }
    setLoading(true); setError(null);
    try { setData(await caseGroupService.getById(id)); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch, refreshKey]);
  return { data, loading, error, refetch: fetch };
};

/** All case-group memberships for a single case (used by the case detail modal's Group Participations section). */
export const useCaseMemberships = (caseId: ID | undefined, refreshKey?: number) => {
  const [data, setData] = useState<CaseMembership[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!caseId) { setData([]); return; }
    setLoading(true); setError(null);
    try { setData(await caseGroupService.getByCase(caseId)); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => { fetch(); }, [fetch, refreshKey]);
  return { data, loading, error, refetch: fetch };
};

export const useCaseGroupCandidates = (params: CaseGroupCandidateSearchRequest, enabled: boolean, refreshKey?: number) => {
  const [data, setData] = useState<CaseGroupCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState(emptyPagination);

  const key = JSON.stringify(params);
  const fetch = useCallback(async () => {
    if (!enabled) return;
    setLoading(true); setError(null);
    try {
      const res = await caseGroupService.getCandidates(params);
      setData(res.data); setPagination(res.pagination);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  useEffect(() => { fetch(); }, [fetch, refreshKey]);
  return { data, loading, error, pagination, refetch: fetch };
};
